/*
 *  Product Swatches
 *    Scripts for the product swatches
 *
 *  Version:
 *    1.0.0 - 2020/11/22
 */
import { handlize } from '@process-creative/slate-theme-tools';
import { Variant } from '../../tools/product/variant';
import {
  getVariant,
  VariantSelector,
  variantSelectorFind,
  variantSelectorSetVariant,
  variantSelectorWatch,
  WithVariantSelector,
} from '../../objects/product/variant-selector';
import { CLASS_SELECTED, CLASS_SOLD_OUT } from '../../tools/constants/classes';
import { ButtonAddToCart } from './product-utils';

let isNotExistent = false;
// Types
type WithSwatchContainer = { swatchContainer: HTMLElement };
type WithSwatches = { swatches: ProductSwatches };
type ProductSwatches = {
  swatchContainer: HTMLElement;
  variantSelector: VariantSelector;
  variantWatcher: ReturnType<typeof variantSelectorWatch>;
  options: string[];
  groups: HTMLDivElement[];
  objects: HTMLButtonElement[];
  selects: HTMLSelectElement[];
};

type FindParams = { container: HTMLElement };
type CreateParams = WithSwatchContainer & WithVariantSelector;
type SetOptionValueParams = WithSwatches & {
  value: string;
  position: number;
  e: Event;
};

// Selectors
const SELECTOR_SWATCH_GROUP = '[data-swatch-group]';
const SELECTOR_SWATCH_OBJ = '[data-swatch-object]';
const SELECTOR_SWATCH_SELECT = '[data-swatch-selector]';
const SELECTOR_SWATCH_VALUE = '[data-swatch-value]';
const SELECTOR_SWATCHES = '[data-swatches]';

// Attributes
const ATTR_POSITION = 'data-swatch-position';
const ATTR_VALUE = 'data-value';

export const swatchesFind = (params: FindParams) => {
  // Find the variant selector
  const variantSelector = variantSelectorFind(params);
  if (!variantSelector) return null;

  const swatches = swatchesCreate({
    swatchContainer: params.container,
    variantSelector,
  });

  return swatches;
};

// This first run when the product template loades
const swatchesCreate = (params: CreateParams) => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const variantInUrl = urlParams.get('variant');
  const { swatchContainer } = params;
  const swatches = {
    ...params,
    options: [],
    groups: [],
    objects: [],
    selects: [],
    variantWatcher: variantSelectorWatch({
      ...params,
      variantChange: (changeParams) => {},
    }),
  } as ProductSwatches;

  // Swatch groups
  const groups = swatchContainer.querySelectorAll<HTMLDivElement>(
    SELECTOR_SWATCH_GROUP
  );
  const swatchesElement =
    swatchContainer.querySelector<HTMLDivElement>(SELECTOR_SWATCHES);

  groups.forEach((group) => {
    const objects =
      group.querySelectorAll<HTMLButtonElement>(SELECTOR_SWATCH_OBJ);
    const select = group.querySelector<HTMLSelectElement>(
      SELECTOR_SWATCH_SELECT
    );
    const positionOne = parseInt(group.getAttribute(ATTR_POSITION) || '');

    if (isNaN(positionOne)) return;

    const position = positionOne - 1;

    swatches.groups.push(group);

    const groupName = group.getAttribute('data-swatch-option-name');
    if (groupName === 'Color' || groupName === 'Colour') {
      objects.forEach((obj) => {
        obj.addEventListener('mouseover', (e) => {
          const title = obj.getAttribute('title') || '';
          const swatchValueLabel = document.querySelector<HTMLSpanElement>(
            '[data-swatch-value]'
          );
          if (swatchValueLabel) {
            swatchValueLabel.innerHTML = title?.toLowerCase() || '';
          }
        });
        obj.addEventListener('mouseout', (e) => {
          const swatchValueLabel = document.querySelector<HTMLSpanElement>(
            '[data-swatch-value]'
          );
          const displayedSwatch = swatchValueLabel?.getAttribute(
            'data-displayed-variant'
          );
          if (swatchValueLabel) {
            swatchValueLabel.innerHTML = displayedSwatch?.toLowerCase() || '';
          }
        });
      });
    }

    // Button style
    objects.forEach((obj) => {
      const value = obj.getAttribute(ATTR_VALUE);
      if (!value) return;
      swatches.objects.push(obj);

      obj.addEventListener('click', (e) => {
        e.preventDefault();
        swatchSetOptionValue({ swatches, position, value, e });
      });

      // On load we get the selected variant to check if the customer had selected a different colour in the collection page
      let preselect = false;

      if (
        (swatchesElement &&
          swatchesElement.dataset?.swatchesPreselectColour === '') ||
        variantInUrl
      ) {
        // Pre-select the swatch if the option group is colour, and the customer have preselected a colour. Example: when they have clicked on a colour in the collection page and then click on the product to go to the PDP
        const preselectedVariant = getVariant();
        if (preselectedVariant && variantInUrl) {
          if (
            preselectedVariant.options[position] ===
              obj.getAttribute('title') &&
            ['Color', 'Colour'].includes(group.dataset.swatchOptionName || '')
          ) {
            preselect = true;
          }
        }
      }

      // Preselect if is the only option in the group, eg. only one size
      if (objects.length == 1) {
        preselect = true;
      }

      if (preselect) {
        obj.click();
      }
    });

    // Select style
    if (select) {
      swatches.selects.push(select);
      select.addEventListener('change', (e) => {
        swatchSetOptionValue({ swatches, position, value: select.value, e });
      });
    }
  });

  return swatches;
};

export const getIsNotExistent: () => boolean = () => {
  return isNotExistent;
};

export const getallSelectedOptionsObjects: (
  objects: HTMLButtonElement[]
) => HTMLButtonElement[] = (objects) => {
  return objects.filter((object) => {
    return object.classList.contains(CLASS_SELECTED);
  });
};

export const getAllSelectedOptions: (
  allSelectedOptionsObjects: HTMLButtonElement[]
) => { position: number; value: string }[] = (allSelectedOptionsObjects) => {
  return allSelectedOptionsObjects.map((buttonSwatch) => {
    return {
      position: Number(buttonSwatch.dataset.swatchPosition) - 1,
      value: buttonSwatch.dataset.value || '',
    };
  });
};

// This is the action that runs when a swatch is clicked
export const swatchSetOptionValue = (params: SetOptionValueParams) => {
  // Note: we account for selecting and deselecting the clicked swatch
  const { swatches, value, position, e } = params;
  const { variantSelector } = swatches;
  const { product } = variantSelector;

  swatches.objects.forEach((object) => {
    object.removeAttribute('disabled');
  });

  const currentSwatchButton = e.currentTarget as HTMLButtonElement;

  const swatchValueLabel =
    swatches.swatchContainer.querySelector<HTMLSpanElement>(
      `${SELECTOR_SWATCH_VALUE}[${ATTR_POSITION}="${position + 1}"]`
    );
  // Update the selected state of the clicked swatch
  if (currentSwatchButton.classList.contains(CLASS_SELECTED)) {
    currentSwatchButton.classList.remove(CLASS_SELECTED);
  } else {
    currentSwatchButton.classList.add(CLASS_SELECTED);
    // Update the colour swatch label to selected
    if (swatchValueLabel) {
      const title = currentSwatchButton.getAttribute('title');
      swatchValueLabel.innerHTML = title?.toLowerCase() || '';
      swatchValueLabel.setAttribute(
        'data-displayed-variant',
        title?.toLowerCase() || ''
      );
    }
  }

  //Get all the options in the same group as the clicked swatch, except the selected swatch
  const selectedOptionsInGroup = swatches.objects.filter((object) => {
    return (
      object.classList.contains(CLASS_SELECTED) &&
      object.dataset.swatchPosition === `${position + 1}` &&
      object.dataset.value !== value
    );
  });

  // Deselect all options in current swatches group
  selectedOptionsInGroup.forEach((option) => {
    option.classList.remove(CLASS_SELECTED);
  });

  // Get all the selected options
  const allSelectedOptionsObjects = getallSelectedOptionsObjects(
    swatches.objects
  );
  const allSelectedOptions = getAllSelectedOptions(allSelectedOptionsObjects);

  allSelectedOptionsObjects.map((buttonSwatch) => {
    // Reset sold out class on selected options that are not in the current clicked group
    const optionPosition = Number(buttonSwatch.dataset.swatchPosition) - 1;

    if (optionPosition !== position) {
      buttonSwatch.classList.remove(CLASS_SOLD_OUT);
    }
  });

  // Now we check the non-selected options, make sure there are existing values and inventory available
  swatches.objects
    .filter((object) => {
      return !object.classList.contains(CLASS_SELECTED);
    })
    .forEach((buttonSwatch) => {
      const optionPosition = Number(buttonSwatch.dataset.swatchPosition) - 1;
      const optionValue = buttonSwatch.dataset.value;
      const results = product.variants.filter((variant) => {
        let hasOtherOptions = true;
        if (handlize(variant.options[optionPosition]) === optionValue) {
          allSelectedOptions.forEach((selectedOption) => {
            if (optionPosition !== selectedOption.position) {
              if (
                handlize(variant.options[selectedOption.position]) !==
                selectedOption.value
              ) {
                hasOtherOptions = false;
              }
            }
          });
          return hasOtherOptions;
        } else {
          return false;
        }
      });

      isNotExistent = false;
      if (results.length === 0) {
        // Here the combination of selected options doesn't exist at all
        // buttonSwatch.setAttribute('disabled','disabled');
        buttonSwatch.classList.add(CLASS_SOLD_OUT);
      } else {
        const firstAvailableVariant = results.find((item) => {
          return item.available;
        });

        if (firstAvailableVariant) {
          buttonSwatch.classList.remove(CLASS_SOLD_OUT);
        } else {
          buttonSwatch.classList.add(CLASS_SOLD_OUT);
        }
      }
    });

  let selectedVariant: Variant | null = null;
  if (product.options.length === allSelectedOptions.length) {
    selectedVariant =
      product.variants.find((variant) => {
        let selected = true;
        allSelectedOptions.forEach((option) => {
          if (handlize(variant.options[option.position]) !== option.value) {
            selected = false;
          }
        });
        return selected;
      }) || null;
    if (selectedVariant) {
      // Mark as sold out all selected swatches for UX
      if (!selectedVariant.available) {
        allSelectedOptionsObjects.forEach((buttonSwatch) => {
          buttonSwatch.classList.add(CLASS_SOLD_OUT);
        });
      }

      variantSelectorSetVariant({
        variantSelector,
        variantId: selectedVariant.id,
        product,
      });
    } else {
      isNotExistent = true;
      allSelectedOptionsObjects.forEach((buttonSwatch) => {
        buttonSwatch.classList.add(CLASS_SOLD_OUT);
      });
    }
  } else {
    if (allSelectedOptions.length > 0) {
      // Get the available variants that matches the current selected options, this is to calculate the best price to show
      const availableVariants = variantSelector.product.variants.filter(
        (variant) => {
          return allSelectedOptions.every((option) => {
            return handlize(variant.options[option.position]) === option.value;
          });
        }
      );
      // Now we find the variant with the smallest price, if available then we update the selected variant, this updates the pricing.
      // Example on when this is needed https://www.solbari.com/collections/solbari-upf50-umbrellas/products/women-compact-sun-umbrella
      // The umbrella comes in 2 sizes 42 and 46, each size has a different price. In order to get the best experience,
      // when the customer selects a the size 46 without selecting the colour, we want to show the minimum price that is available
      // from any of the posible combinations with this size selected. In the previous version of the site, if the customer had selected 46, the price
      // wouldn't change until the customer had selected all the options required for the variant to be updated
      const smallestPriceVariant = availableVariants.reduce((prev, current) =>
        current.price < prev.price ? current : prev
      );
      if (smallestPriceVariant) {
        variantSelectorSetVariant({
          variantSelector,
          variantId: smallestPriceVariant.id,
          product,
        });
      }
    } else {
      // If now option is selected we want to show the cheapest variant, this is required because the url of the pdp product might have a variant id,
      // if the variant id is from a product that is more expensive
      // the customer will get the wrong idea that the price is randomly shown
      const smallestPriceVariant = variantSelector.product.variants.reduce(
        (prev, current) => (current.price < prev.price ? current : prev)
      );
      if (smallestPriceVariant) {
        variantSelectorSetVariant({
          variantSelector,
          variantId: smallestPriceVariant.id,
          product,
        });
      }
    }
  }

  // Now we get selected colour and selected type, if none selected we set the default. This is to update the images in the carousel based on the current selection
  const colourOptionPosition = product.options.findIndex((option) => {
    return ['color', 'colour'].includes(handlize(option.name));
  });

  const typeOptionPosition = product.options.findIndex((option) => {
    return ['type'].includes(handlize(option.name));
  });

  let selectedColourValue: string | null = null;
  let selectedTypeValue: string | null = null;

  if (colourOptionPosition > -1) {
    const selectedColour = swatches.objects.find((object) => {
      return (
        object.classList.contains(CLASS_SELECTED) &&
        object.dataset.swatchPosition === `${colourOptionPosition + 1}`
      );
    });
    if (selectedColour) {
      selectedColourValue = selectedColour.dataset?.value || '';
    } else {
      const selectedColour = swatches.objects.find((object) => {
        return object.dataset.swatchPosition === `${colourOptionPosition + 1}`;
      });
      selectedColourValue = selectedColour?.dataset?.value || '';
    }
  }

  if (typeOptionPosition > -1) {
    const selectedType = swatches.objects.find((object) => {
      return (
        object.classList.contains(CLASS_SELECTED) &&
        object.dataset.swatchPosition === `${typeOptionPosition + 1}`
      );
    });
    if (selectedType) {
      selectedTypeValue = selectedType.dataset?.value || '';
    } else {
      const selectedType = swatches.objects.find((object) => {
        return object.dataset.swatchPosition === `${typeOptionPosition + 1}`;
      });
      selectedTypeValue = selectedType?.dataset?.value || '';
    }
  }

  // On variant change, refresh swatches
  window.dispatchEvent(
    new CustomEvent<any>('product-swatch-selected', {
      detail: {
        variant: selectedVariant,
        colour: selectedColourValue,
        type: selectedTypeValue,
      },
    })
  );
};
